import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, LineChart, Package } from "lucide-react";
import { TableCell, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { TruncatedText } from "@/components/ui/truncated-text";
import { RoutePath } from "@/routes";
import type { DailyRanksProduct, MetricKey } from "@/types/rankTracker";
import { KpsBreakdownPopover } from "@/components/keyword-bank/KpsBreakdownPopover";
import { buildKpsRollUpViewModel } from "../kpsViewModel";
import { RankHeatCell } from "./RankHeatCell";
import type { RankType } from "./RankTypeToggle";
import { METRIC_CELL_TOOLTIPS, formatDateAdded, formatMetricValue } from "./constants";

export interface ProductRowProps {
  product: DailyRanksProduct;
  /** `days` from the daily-ranks response -- one heat cell per entry, same array the theme and
   * keyword rows below it are built from. */
  days: string[];
  isExpanded: boolean;
  onToggleExpand: () => void;
  /**
   * WEB-004 / FR-7. A product row always shows its **organic** roll-up rank — never a sponsored one.
   * In Sponsored view (`"sponsored"`) its day cells dim to 35% opacity; Organic and Both views
   * render them exactly as today. */
  rankType?: RankType;
  /** Fired by the chart-icon button in this row's chart cell -- WEB-006, a second control distinct
   * from the `View {product.name}` thumbnail button above (which keeps navigating to
   * `/products/{id}`). `RankHeatmapTable` constructs the `level: 'product'` `RankDetailTarget` arm
   * (this row's own `product`, no theme and no keyword) around this call. */
  onOpenDetail?: () => void;
}

/**
 * One of the five roll-up metric cells (`KPS` · `Search Vol.` · `Kw. Sales` · `Comp.` · `Rel.`).
 * Renders `metrics[metric].value` via the same `formatMetricValue` a keyword row uses, and carries
 * `METRIC_CELL_TOOLTIPS[metric]`'s per-cell tooltip (FR-6) -- the roll-up heat cell beside it is the
 * precedent for the affordance, not an implementation this extends: these cells rendered plain text
 * before WEB-002. `keywordCount` is always the row's own wire count (`product.keywordCount` here).
 */
function ProductMetricCell({
  metric,
  rollUp,
  keywordCount,
}: {
  metric: MetricKey;
  rollUp: { value: number | null; keywordCount: number };
  keywordCount: number;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <TableCell className="px-3 text-right text-muted-foreground tabular-nums">
          {formatMetricValue(rollUp.value)}
        </TableCell>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-left">
        {METRIC_CELL_TOOLTIPS[metric](rollUp.keywordCount, keywordCount)}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * The `KPS` roll-up cell specifically -- same value and tooltip as `ProductMetricCell` above, plus
 * the breakdown control (FR-8). Kept separate from `ProductMetricCell` for the same reason as
 * `ThemeRow`'s equivalent split: the other four roll-up metrics carry no breakdown at all.
 */
function ProductKpsMetricCell({ product }: { product: DailyRanksProduct }) {
  const rollUp = product.metrics.kps;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <TableCell className="px-3 text-right text-muted-foreground tabular-nums">
          <div className="flex items-center justify-center gap-1.5">
            {formatMetricValue(rollUp.value)}
            {/* Stops the click from reaching the row's own onClick, which toggles expand/collapse
                (FR-11) -- same pattern as the expand button and the ASIN link below. */}
            <span onClick={(e) => e.stopPropagation()}>
              <KpsBreakdownPopover
                viewModel={buildKpsRollUpViewModel(product.name, product.metrics, product.kpsRollUp)}
              />
            </span>
          </div>
        </TableCell>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-left">
        {METRIC_CELL_TOOLTIPS.kps(rollUp.keywordCount, product.keywordCount)}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * The hero-product row: label, the chart-icon button (WEB-006), the five roll-up metric cells
 * (FR-4), its own `Date Added` (the date this product was added to Signal, or the absence mark --
 * AUT-382), one roll-up heat cell per day, and no checkbox
 * (`components/rank-tracker.md § Target state -- the screen`).
 */
export function ProductRow({ product, days, isExpanded, onToggleExpand, rankType = "organic", onOpenDetail }: ProductRowProps) {
  const navigate = useNavigate();

  return (
    <TableRow className="cursor-pointer" onClick={onToggleExpand}>
      {/* w-[340px]/py-2 (item 9) and the 80px thumbnail (item 8) below are what make this row settle
          at the mockup's measured 97px -- no explicit row height anywhere. WEB-010 (design-conformance
          pass, item 3): the mockup's product label cell is the plain card surface, not the
          surface-tint-soft this used to carry -- `TableCell sticky` already paints the opaque
          bg-card, so dropping the tint is all that is needed to match theme/keyword rows' white. */}
      <TableCell sticky className="w-[340px] py-2">
        <div className="flex items-center gap-2 min-w-0">
          <button
            type="button"
            aria-label={isExpanded ? `Collapse ${product.name}` : `Expand ${product.name}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            className="shrink-0 text-muted-foreground"
          >
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>

          <button
            type="button"
            aria-label={`View ${product.name}`}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`${RoutePath.Products}/${product.id}`);
            }}
            className="shrink-0"
          >
            {product.imageUrl ? (
              <img
                src={product.imageUrl}
                alt=""
                className="h-20 w-20 rounded-md border border-border bg-card object-contain"
              />
            ) : (
              <span className="flex h-20 w-20 items-center justify-center rounded-md border border-border bg-card text-muted-foreground">
                <Package className="h-4 w-4" />
              </span>
            )}
          </button>

          <div className="min-w-0">
            {/* WEB-010 (design-conformance pass, item 4): two lines before truncating, not one. */}
            <TruncatedText className="font-medium" lines={2}>
              {product.name}
            </TruncatedText>
            <a
              href={`https://www.amazon.com/dp/${product.asin}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-xs text-primary hover:underline"
            >
              {product.asin}
            </a>
          </div>

          {/* WEB-002: the roll-up Ad badge. Derived from the keywords present after filtering
              (`.some` across this product's themes), matching the prototype -- so "Not advertised"
              hides it with its advertised keywords, rather than reading the server's full-set
              `product.hasAdSpend` (which stays true). Equivalent to the server roll-up when
              unfiltered. Sponsored-Products accent colour, as the theme/keyword rows. */}
          {product.themes.some((theme) => theme.keywords.some((keyword) => keyword.hasAdSpend)) && (
            <Badge
              variant="secondary"
              className="shrink-0 cursor-default bg-[hsl(var(--heat-sp-4))] text-[hsl(var(--heat-sp-on-light))] hover:bg-[hsl(var(--heat-sp-4))]"
              title="Ad spend on one or more keywords"
              data-testid="ad-badge"
            >
              Ad
            </Badge>
          )}
        </div>
      </TableCell>

      {/* Chart column -- a second, separate control from the thumbnail button above (which keeps
          navigating to `/products/{id}`): same px-0/text-center cell shape, LineChart icon, h-6 w-6
          hit area, hover treatment and stopPropagation as KeywordRow's (KeywordRow.tsx:78), so the
          click opens the detail view without also toggling this row's own expand/collapse. */}
      <TableCell className="px-0 text-center">
        <button
          type="button"
          aria-label={`View rank chart for ${product.name}`}
          onClick={(e) => {
            e.stopPropagation();
            onOpenDetail?.();
          }}
          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-primary transition-colors hover:bg-muted"
        >
          <LineChart className="h-4 w-4" />
        </button>
      </TableCell>

      <ProductKpsMetricCell product={product} />
      <ProductMetricCell
        metric="searchVolume"
        rollUp={product.metrics.searchVolume}
        keywordCount={product.keywordCount}
      />
      <ProductMetricCell
        metric="keywordSales"
        rollUp={product.metrics.keywordSales}
        keywordCount={product.keywordCount}
      />
      <ProductMetricCell
        metric="competitors"
        rollUp={product.metrics.competitors}
        keywordCount={product.keywordCount}
      />
      <ProductMetricCell
        metric="relevance"
        rollUp={product.metrics.relevance}
        keywordCount={product.keywordCount}
      />

      {/* AUT-497 slice B: the Ad push column. A product row renders an empty cell (not a pill, not
          `–`, not a roll-up) -- a per-keyword ad-push suggestion does not exist for a roll-up row
          (Decision D-4). The column's 140px width comes from the header row under `table-fixed`. */}
      <TableCell />

      {days.map((day, i) => (
        <RankHeatCell
          key={day}
          day={day}
          position={product.positions[i]}
          variant="rollup"
          keywordCount={product.keywordCount}
          count={product.counts[i]}
          dim={rankType === "sponsored"}
        />
      ))}

      <TableCell className="px-3 text-left text-muted-foreground whitespace-nowrap border-l border-border">
        {formatDateAdded(product.dateAdded)}
      </TableCell>
    </TableRow>
  );
}
